/// <reference types="vite/client" />

import type { AxiosRequestConfig } from 'axios'
import axios from 'axios'
import * as R from 'remeda'

import { sessionStore } from '@gto/gw-store'
import { QueryCache } from '@tanstack/query-core'

const baseURL: unknown =
  import.meta.env.MODE === 'localdev' && import.meta.env.VITE_PROXY_URL !== undefined
    ? '' // for dev server proxy we need to send relative requests so they target dev server
    : import.meta.env.VITE_API_BASE_URL

if (typeof baseURL !== 'string') {
  throw new Error('Missing env variable VITE_API_BASE_URL')
}

let isRefreshing = false
let failedRequests: { resolve: (value: unknown) => void; reject: (reason?: unknown) => void }[] = []

const processQueue = () => {
  failedRequests.forEach(({ resolve }) => {
    resolve(true)
  })
  failedRequests = []
}

export const axiosInstance = axios.create({
  baseURL,
})

async function refreshAccessToken() {
  isRefreshing = true
  const refresh = localStorage.getItem('user_refresh')

  const response: { data: { access: string } } = await axios.post(
    `${baseURL}/v1/token/refresh/`,
    R.isNonNullish(refresh)
      ? {
          refresh,
        }
      : undefined,
  )

  const { access } = response.data
  sessionStore.getState().setAccessToken(access)
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${access}`
  isRefreshing = false

  return access
}

function handleTokenRefreshFailure(error: unknown) {
  isRefreshing = false
  handleAuthFailure(error)
  return Promise.reject(new Error('Token refresh failed'))
}

axiosInstance.interceptors.request.use(async request => {
  const accessToken = sessionStore.getState().accessToken

  // If no access token, send request to refresh token
  if (R.isNullish(accessToken) || R.isEmpty(accessToken)) {
    try {
      const newAccessToken = await refreshAccessToken()
      request.headers.Authorization = `Bearer ${newAccessToken}`
    } catch (error) {
      handleTokenRefreshFailure(error)
    }
  } else {
    request.headers.Authorization = `Bearer ${accessToken}`
  }

  request.headers.GWCLIENTID = sessionStore.getState().appUid

  /** Removes square brackets [] from query params
   * I.e before it was ?ordering[]=total_hands&ordering[]=original_name
   * Now it is ?ordering=total_hands&ordering=original_name
   */
  request.paramsSerializer = {
    indexes: null,
  }

  return request
})

axiosInstance.interceptors.response.use(
  response => response,
  async error => {
    if (axios.isAxiosError(error)) {
      const originalRequest = error.config
      if (originalRequest && error.response?.status === 401) {
        if (isRefreshing) {
          try {
            await new Promise((resolve, reject) => {
              failedRequests = [...failedRequests, { resolve, reject }]
            })
            return await axiosInstance(originalRequest)
          } catch (e) {
            return e
          }
        }

        try {
          await refreshAccessToken()
          processQueue()
          //Refreshing token failed
        } catch (error) {
          handleTokenRefreshFailure(error)
        }

        try {
          return await axiosInstance(originalRequest)
          //Original request failed
        } catch (error) {
          // Only handle auth failure if the refresh token is still invalid
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            handleAuthFailure(error)
            return Promise.reject(new Error('Token refresh failed'))
          }
          return error instanceof Error
            ? Promise.reject(error)
            : Promise.reject(new Error('Original request failed after the token was refreshed'))
        }
      }

      return Promise.reject(error)
    }
    return Promise.reject(new Error(typeof error === 'string' ? error : 'Data fetching error'))
  },
)

function handleAuthFailure(error: unknown) {
  console.error('Token refresh failed:', error)
  window.location.href = '/login'
}

export const customInstance = <T>(config: AxiosRequestConfig): Promise<T> =>
  axiosInstance<T>({ ...config }).then(({ data }) => data)

export default customInstance

// make sure queryCache has only one instance across Vue and React
// @ts-ignore
globalThis._gwQueryCache = globalThis._gwQueryCache ?? new QueryCache()
// @ts-ignore
export const queryCache = (globalThis._gwQueryCache as QueryCache | undefined) ?? new QueryCache()
